
import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Container,
  Box,
  TextField,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Checkbox,
  TablePagination,
  Button,
  IconButton,
  Menu,
  MenuItem as MuiMenuItem,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import api from "../../services/api";
import { BASE_URL } from "../../services/configUrls";
import { useTheme } from "@mui/material/styles";

/* ---------- TOAST HELPERS ---------- */
const handleSuccessMessage = (message) =>
  toast.success(message, { autoClose: 2000, position: "top-center" });

const handleErrorMessage = (message) =>
  toast.error(message, { autoClose: 3000, position: "top-center" });

const getAppliedReasonMessage = (reason) => {
  if (!reason || typeof reason !== "string") {
    return "Your students have already applied in this domain, so you cannot unapprove it.";
  }
  if (
    reason.toLowerCase().includes("students applied") ||
    reason.toLowerCase().includes("applied internship")
  ) {
    return "Your students have already applied in this domain, so you cannot unapprove it.";
  }
  return reason;
};

function AssignDomain() {
  const theme = useTheme();
  const [domains, setDomains] = useState([]);
  const [filteredDomains, setFilteredDomains] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selected, setSelected] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuDomain, setMenuDomain] = useState(null);
  const [canUpdate, setCanUpdate] = useState(false);
  const [currentCohort, setCurrentCohort] = useState(null);

  /* ---------- PAGINATION ---------- */
  const paginatedRows = useMemo(
    () =>
      filteredDomains.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [filteredDomains, page, rowsPerPage]
  );

  /* ---------- FETCH APPROVAL STATE ---------- */
  const fetchApprovalState = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const res = await api.get(
        `${BASE_URL}/institute/spoc/domain/approval-state`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCanUpdate(res.data.can_update);
    } catch (err) {
      console.error(err);
    }
  };

  /* ---------- FETCH DOMAINS ---------- */
  const fetchAssignedDomains = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("accessToken");
      const res = await api.get(
        `${BASE_URL}/institute/spoc/assigned-domains/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setDomains(res.data.data || []);
      setFilteredDomains(res.data.data || []);
      setCurrentCohort(res.data.current_cohort);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- INITIAL LOAD ---------- */
  useEffect(() => {
    fetchApprovalState();
    fetchAssignedDomains();
  }, []);

  /* ---------- SEARCH + FILTER ---------- */
  useEffect(() => {
    let data = [...domains];

    if (search) {
      data = data.filter((d) =>
        d.domain_name.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (statusFilter !== "All") {
      data = data.filter((d) =>
        statusFilter === "Approved"
          ? d.status === "1"
          : d.status === "0"
      );
    }

    setFilteredDomains(data);
    setPage(0);
    setSelected([]);
  }, [search, statusFilter, domains]);

  /* ---------- SELECTION ---------- */
  const handleSelectAll = (e) => {
    if (!canUpdate) return;

    const pageIds = paginatedRows
      .filter((d) => d.can_update !== false)
      .map((d) => d.domain_id);

    setSelected(e.target.checked ? pageIds : []);
  };

  const handleSelectOne = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const isSelected = (id) => selected.includes(id);

  const isAllPageSelected =
    paginatedRows.length > 0 &&
    paginatedRows.every(
      (d) => d.can_update === false || selected.includes(d.domain_id)
    );

  /* ---------- SINGLE UPDATE ---------- */
  const toggleStatus = async (row) => {
    if (!row) return;

    if (!canUpdate) {
      handleErrorMessage("Domain approval is currently locked.");
      return;
    }

    if (row.can_update === false) {
      handleErrorMessage(getAppliedReasonMessage(row.reason));
      setAnchorEl(null);
      setMenuDomain(null);
      return;
    }

    const newStatus = row.status === "1" ? "0" : "1";

    try {
      const token = localStorage.getItem("accessToken");

      await api.put(
        `${BASE_URL}/institute/spoc/domain/spoc-approval/bulk`,
        { domain_ids: [row.domain_id], spoc_approved: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      handleSuccessMessage("Domain status updated successfully");
      await fetchAssignedDomains();
    } catch (err) {
      const errorMsg =
        err.response?.data?.reason ||
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Failed to update domain";

      handleErrorMessage(getAppliedReasonMessage(errorMsg));
    } finally {
      setAnchorEl(null);
      setMenuDomain(null);
    }
  };

  /* ---------- BULK UPDATE ---------- */
  const bulkUpdateStatus = async (approve) => {
    if (!canUpdate || selected.length === 0) return;

    try {
      const token = localStorage.getItem("accessToken");
      const spoc_approved = approve ? "1" : "0";

      const res = await api.put(
        `${BASE_URL}/institute/spoc/domain/spoc-approval/bulk`,
        { domain_ids: selected, spoc_approved },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      handleSuccessMessage(
        res.data.message || `${selected.length} domains updated successfully`
      );

      setSelected([]);
      await fetchAssignedDomains();
    } catch (err) {
      const errorMsg =
        err.response?.data?.reason ||
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Bulk update failed";

      handleErrorMessage(getAppliedReasonMessage(errorMsg));
    }
  };

  /* ---------- UI ---------- */
  return (
    <Container maxWidth="lg" sx={{ mt: 3 }}>
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, fontSize: "1.1rem", color: "#0288D1", p: 1 }}
        >
          Assigned Domains
        </Typography>

        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search by domain"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 280, "& .MuiOutlinedInput-root": { borderRadius: "50px" } }}
          />
          <Select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ width: 180, borderRadius: "50px" }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Approved">Approved</MenuItem>
            <MenuItem value="Not Approved">Unapproved</MenuItem>
          </Select>
        </Box>
        {/* 👇 Add here */}
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  backgroundColor: (theme) => theme.palette.info.light + "10", // lighter (adds transparency)
                  borderRadius: "6px",
                  border: (theme) => `1px solid ${theme.palette.info.main}10`,
                }}
              >
                <Typography variant="body2">
                  <span style={{ color: theme.palette.error.main, fontWeight: 600 }}>
                    Note:
                  </span>{" "}
                Registration status indicates whether enrollment for Cohort{" "}
                <span style={{ fontWeight: 600 }}>
                {currentCohort || "N/A"}
                </span>{" "}
                is open or closed.
                </Typography>
              </Box>
      </Paper>

      {selected.length > 0 && (
        <Box mb={2}>
          {(statusFilter === "All" || statusFilter === "Not Approved") && (
            <Button
              onClick={() => bulkUpdateStatus(true)}
              color="success"
              variant="contained"
              sx={{ mr: 1, textTransform: "none" }}
            >
              Approve
            </Button>
          )}

          {(statusFilter === "All" || statusFilter === "Approved") && (
            <Button
              onClick={() => bulkUpdateStatus(false)}
              color="error"
              variant="contained"
              sx={{ textTransform: "none" }}
            >
              Unapprove
            </Button>
          )}
        </Box>
      )}


      <Paper>
        {loading ? (
          <Box p={2} textAlign="center">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead sx={{bgcolor:"#60a4e8ff"}}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isAllPageSelected}
                      onChange={handleSelectAll}
                      disabled={!canUpdate}
                     sx={{
                      color:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.7)"
                          : "rgba(0, 0, 0, 0.7)", // dark color for light mode
                      "&.Mui-checked": {
                        color:
                          theme.palette.mode === "dark" ? "#fff" : "#000", // default primary in light mode
                      },
                    }}
                    />

                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold'}}>Domain Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold'}}>Registration</TableCell>
                  <TableCell sx={{ fontWeight: 'bold'}}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold'}}>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
              {paginatedRows.length > 0 ? (
                paginatedRows.map((row) => (
                  <TableRow key={row.domain_id}>
                    <TableCell padding="checkbox">
                      <Tooltip
                        title={
                          row.can_update === false
                            ? getAppliedReasonMessage(row.reason)
                            : !canUpdate
                            ? "Domain updates are locked"
                            : ""
                        }
                        arrow
                        disableHoverListener={canUpdate && row.can_update !== false}
                      >
                        <span>
                          <Checkbox
                            checked={isSelected(row.domain_id)}
                            onChange={() => handleSelectOne(row.domain_id)}
                            disabled={!canUpdate || row.can_update === false}
                            sx={{
                              color:
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.7)"
                                  : "rgba(0, 0, 0, 0.7)",
                              "&.Mui-checked": {
                                color: theme.palette.mode === "dark" ? "#fff" : "#000",
                              },
                            }}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.domain_name}</TableCell>
                     <TableCell
                      sx={{
                        color: row.reg_open === "1" ? "green" : "red",
                        fontWeight: 600,
                      }}
                    >
                      {row.reg_open === "1" ? "Open" : "Closed"}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: row.status === "1" ? "green" : "red",
                        fontWeight: 600,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <span>{row.status === "1" ? "Approved" : "Unapproved"}</span>
                        {row.can_update === false && (
                          <Tooltip
                            title={getAppliedReasonMessage(row.reason)}
                            arrow
                          >
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: "1rem",
                                color: "#f59e0b",
                                cursor: "pointer",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setMenuDomain(row);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    No Data Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

            </Table>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              PaperProps={{
                sx: { minWidth: 200 }
              }}
            >
              <Tooltip
                title={
                  menuDomain?.can_update === false
                    ? getAppliedReasonMessage(menuDomain?.reason)
                    : !canUpdate
                    ? "Domain updates are locked"
                    : ""
                }
                arrow
              >
                <div>
                  <MuiMenuItem
                    onClick={() => {
                      if (menuDomain?.can_update === false) {
                        handleErrorMessage(
                          getAppliedReasonMessage(menuDomain?.reason)
                        );
                        setAnchorEl(null);
                        setMenuDomain(null);
                        return;
                      }
                      toggleStatus(menuDomain);
                    }}
                    disabled={!canUpdate}
                    sx={{
                      ...(menuDomain?.can_update === false && {
                        color: "text.disabled",
                        cursor: "not-allowed",
                      }),
                    }}
                  >
                    {menuDomain?.status === "1" ? "Unapprove" : "Approve"}
                  </MuiMenuItem>
                </div>
              </Tooltip>

              {menuDomain?.can_update === false && (
                <Box
                  sx={{
                    px: 2,
                    py: 0.8,
                    maxWidth: 240,
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "error.main",
                      fontSize: "0.75rem",
                      lineHeight: 1.3,
                      display: "block",
                    }}
                  >
                    {getAppliedReasonMessage(menuDomain?.reason)}
                  </Typography>
                </Box>
              )}
            </Menu>

            <TablePagination
              component="div"
              count={filteredDomains.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(+e.target.value);
                setPage(0);
                setSelected([]);
              }}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>
    </Container>
  );
}

export default AssignDomain;
